import { CSVLink } from 'components/CSVLink';
import { min, range } from 'lodash';
import PropTypes from 'prop-types';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button, Card, CardHeader, Col, Row, Table } from 'reactstrap';
import { ICONS } from '../../../consts/consts';
import { createCSVFileName } from '../../../utils/util/util';
import EmptyState from '../EmptyState';
import CardHeaderTitle from './CardHeaderTitle';

const DEFAULT_ROW_LIMIT = 15;

const TableCell = (props) => {
  const content = useMemo(
    () =>
      typeof props.field.getValue === 'function'
        ? props.field.getValue(props.field.name, props.datum)
        : props.datum,
    [props]
  );
  return (
    <td className={props.field.className} style={props.field.style}>
      {content}
    </td>
  );
};

const DEFAULT_ROWS = [];

const GenericTableCard: FC<Props> = ({
  rowLimit: propsRowLimit = DEFAULT_ROW_LIMIT,
  rows: propsRows = DEFAULT_ROWS,
  showTableHeader = true,
  ...props
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [rowLimit, setRowLimit] = useState(DEFAULT_ROW_LIMIT);
  const [currentSortBy, setCurrentSortBy] = useState(undefined);
  const [sortDirections, setSortDirections] = useState(undefined); // array of booleans, true for ascending

  useEffect(() => {
    if (propsRowLimit != rowLimit) {
      // @ts-expect-error
      setRowLimit(propsRowLimit);
    }
  }, [propsRowLimit, rowLimit]);

  useEffect(() => {
    // @ts-expect-error
    const newSortDirections = props.fields.map((f) =>
      // @ts-expect-error
      f?.defaultSort === 'descending' ? false : true
    );
    // @ts-expect-error
    setSortDirections(newSortDirections);
    // @ts-expect-error
    setCurrentSortBy(props.defaultSortBy);
  }, [props.fields, props.defaultSortBy]);

  const sortedRows = useMemo(() => {
    const compare = (a, b, compareFunc) => {
      if (compareFunc) {
        return compareFunc(a, b);
      }
      if (a < b) return -1;
      if (a > b) return 1;
      return 0;
    };
    // @ts-expect-error
    if (sortDirections !== undefined && propsRows?.length > 0) {
      // @ts-expect-error
      const sortedRowData = [...propsRows];
      sortedRowData.sort(
        (a, b) =>
          compare(
            // @ts-expect-error
            a[currentSortBy],
            // @ts-expect-error
            b[currentSortBy],
            // @ts-expect-error
            props.fields[currentSortBy]?.compare
            // @ts-expect-error
          ) * (sortDirections[currentSortBy] ? 1 : -1)
      );
      return sortedRowData;
    }
    return [];
  }, [sortDirections, propsRows, props.fields, currentSortBy]);

  const TableHeader = useCallback(() => {
    return (
      <thead>
        <tr>
          {/* @ts-expect-error */}
          {props.fields.map(({ name, title, className }, index) => (
            <th
              className={className}
              key={index}
              role="button"
              onClick={() => {
                if (currentSortBy === index) {
                  setSortDirections(
                    // @ts-expect-error
                    sortDirections.map((d, dindex) =>
                      dindex === index ? !d : d
                    )
                  );
                } else {
                  // @ts-expect-error
                  setCurrentSortBy(index);
                }
              }}
            >
              <span className="text-muted sort">{title ?? name}</span>
              {currentSortBy === index ? (
                <>
                  <i
                    className={`fe fe-chevron-${
                      // @ts-expect-error
                      sortDirections[index] ? 'up' : 'down'
                    }`}
                  />
                </>
              ) : (
                <></>
              )}
            </th>
          ))}
        </tr>
      </thead>
    );
  }, [currentSortBy, props.fields, sortDirections]);

  const MoreLink = useCallback(() => {
    return (
      <Button
        className="mb-4"
        color="link"
        onClick={() => {
          // @ts-expect-error
          setRowLimit(isExpanded ? propsRowLimit : propsRows.length + 1);
          setIsExpanded(!isExpanded);
        }}
      >
        <FormattedMessage
          id="app.views.widgets.cards.generic_table_card.show_more_or_less"
          defaultMessage="Show {isExpanded, select, true {less} other {more}}"
          values={{ isExpanded: isExpanded }}
        />
      </Button>
    );
    // @ts-expect-error
  }, [isExpanded, propsRowLimit, propsRows.length]);

  const rowsToShow = useMemo(
    () =>
      isExpanded
        ? // @ts-expect-error
          propsRows.length
        : // @ts-expect-error
          min([rowLimit === 0 ? propsRows.length : rowLimit, propsRows.length]),
    // @ts-expect-error
    [isExpanded, propsRows.length, rowLimit]
  );

  const exportButton = useMemo(() => {
    if (!props.fields?.length || !sortedRows?.length) {
      return '';
    }
    // @ts-expect-error
    const headers = props.fields.map((field) => field.name);
    const rows = sortedRows.map((row) =>
      row!.map((datum, index) =>
        // @ts-expect-error
        props.fields[index].getCSV ? props.fields[index].getCSV(datum) : datum
      )
    );

    return sortedRows?.length ? (
      <CSVLink
        className="btn btn-sm btn-light float-end"
        filename={createCSVFileName()}
        data={rows}
        headers={headers}
      >
        <i className={ICONS.EXPORT + ' me-2'} />
        <FormattedMessage
          id="app.views.widgets.cards.generic_table_card.export_to_csv"
          defaultMessage="Export to CSV"
        />
      </CSVLink>
    ) : (
      <></>
    );
  }, [props.fields, sortedRows]);

  const title = useMemo(
    () =>
      props.showExportButton ? (
        <Row className="align-items-center">
          <Col>{props.title}</Col>
          <Col className="col-auto">{exportButton}</Col>
        </Row>
      ) : (
        props.title
      ),
    [props.showExportButton, props.title, exportButton]
  );

  return !props.fields?.length ? (
    // @ts-expect-error
    <EmptyState title={props.emptyStateText} />
  ) : (
    // @ts-expect-error
    <Card className={props.className} role={props.role} style={props.style}>
      {title && (
        <CardHeader>
          <CardHeaderTitle>{title}</CardHeaderTitle>
        </CardHeader>
      )}
      {/* @ts-expect-error */}
      <Table className={`card-table table-sm ${props.tableClassName}`}>
        {showTableHeader && <TableHeader />}
        {!sortedRows?.length ? (
          <tbody>
            <tr>
              <td colSpan={props.fields.length}>{props.emptyStateText}</td>
            </tr>
          </tbody>
        ) : (
          <tbody className="list">
            {/* @ts-expect-error */}
            {range(rowsToShow).map((index) => (
              <tr key={index} className="p-1">
                {sortedRows[index]!.map((datum, i) => (
                  // @ts-expect-error
                  <TableCell key={i} field={props.fields[i]} datum={datum} />
                ))}
              </tr>
            ))}
          </tbody>
        )}
      </Table>
      {/* @ts-expect-error */}
      {rowLimit !== 0 && propsRows.length > propsRowLimit && <MoreLink />}
    </Card>
  );
};

const GenericTableCard_propTypes = {
  className: PropTypes.object,
  defaultSortBy: PropTypes.number,
  emptyStateText: PropTypes.string,
  fields: PropTypes.arrayOf(PropTypes.object),
  rowLimit: PropTypes.number,
  rows: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.any)),
  showExportButton: PropTypes.bool,
  showTableHeader: PropTypes.bool,
  style: PropTypes.object,
  title: PropTypes.string,
};

type Props = PropTypes.InferProps<typeof GenericTableCard_propTypes>;

export default React.memo(GenericTableCard);
